import React, { useEffect, useState } from 'react'
import { ImageIcon } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import LanguageTranslator from '../../../../locals/LanguageTranslator'
import { dispatchHandler } from '../../../../helper/apiHelperFunctions'

// The first three ranks get a medal, everything after that gets a numbered indigo badge.
const RANK_MEDALS = { 1: "🥇", 2: "🥈", 3: "🥉" };

const RankBadge = ({ rank }) => {
    const medal = RANK_MEDALS?.[rank];
    return (
        <div
            className={`absolute left-[10px] top-[10px] size-[32px] rounded-full flex items-center justify-center ${medal ? 'bg-white' : 'bg-[#5D5FEF]'}`}
            style={{ filter: medal ? "drop-shadow(0px 2px 4px rgba(0,0,0,0.18))" : "drop-shadow(0px 2px 5px rgba(212,213,248,0.8))" }}
        >
            {medal
                ? <span className='text-[20px] leading-none'>{medal}</span>
                : <span className='text-[13px] font-bold text-white leading-none'>{rank}</span>}
        </div>
    )
}

const DesignCard = ({ item }) => {
    const sellThruWidth = Math.min(item?.percentage || 0, 100);

    return (
        <div className='bg-white rounded-[14px] border border-[#F1F5F9] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col'>
            {/* Product shot placeholder, the real image will come with the api */}
            <div className='relative h-[141px] w-full bg-[#F8FAFC] flex items-center justify-center'>
                <ImageIcon size={28} color='#C7D2E1' strokeWidth={1.4} />
                <RankBadge rank={item?.rank} />
            </div>

            <div className='flex flex-col gap-[10px] px-[12px] pt-[10px] pb-[12px]'>
                <LanguageTranslator tag='p' className='mb-0 capitalize text-[11px] font-normal leading-[1.4] text-[#111D31]'>{item?.design}</LanguageTranslator>

                <div className='flex items-center gap-[8px]'>
                    <div className='flex-1 min-w-0 flex items-baseline gap-[6px]'>
                        <p className='mb-0 text-[14px] font-semibold text-[#111D31]'>{item?.qty}</p>
                        <LanguageTranslator tag='p' className='mb-0 text-[11px] font-normal text-[#4A5D7A]'>Qty</LanguageTranslator>
                    </div>
                    <p className='mb-0 text-[14px] font-semibold text-[#111D31] whitespace-nowrap'>{item?.sharePct}% share</p>
                </div>

                <div className='flex items-center gap-[12px]'>
                    <div className='flex-1 min-w-0 h-[6px] rounded-full bg-[#F1F5F9] overflow-hidden'>
                        <div className='h-full rounded-full' style={{ width: `${sellThruWidth}%`, backgroundColor: '#5D5FEF' }} />
                    </div>
                    <p className='mb-0 text-[14px] font-semibold text-right whitespace-nowrap text-[#1E293B]'>{item?.percentage}%</p>
                </div>
            </div>
        </div>
    )
}

const TopSellingDesigns = () => {
    const dispatch = useDispatch();
    const getBusinessDashboardSummaryData = useSelector(state => state?.digivendStore?.getBusinessDashboardSummaryData || {});

    const [data, setData] = useState([]);

    useEffect(() => {
        dispatchHandler(dispatch, "getBusinessDashboardSummaryRequest", { displayName: "TOP_SELLING_DESIGNS" });
    }, [dispatch]);

    useEffect(() => {
        if (getBusinessDashboardSummaryData?.isSuccess) {
            const resource = getBusinessDashboardSummaryData?.data?.data?.resource || getBusinessDashboardSummaryData?.data?.resource || [];

            setData(resource.map((item, idx) => ({
                rank: item?.rank || idx + 1,
                design: item?.design,
                qty: (Number(item?.sales_qty) || 0).toLocaleString('en-IN'),
                sharePct: item?.share_pct,
                percentage: item?.sell_thru_pct,
            })));
        }
    }, [getBusinessDashboardSummaryData?.isSuccess, getBusinessDashboardSummaryData?.data]);

    return (
        <div className={`bg-white rounded-[14px] border border-[#F1F5F9] shadow-[0px_1px_1.5px_0px_rgba(0,0,0,0.1),0px_1px_1px_0px_rgba(0,0,0,0.1)] p-[21px] ${getBusinessDashboardSummaryData?.isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <p className='mb-0 text-[13px] font-semibold text-fontBlack'>
                <LanguageTranslator>Top Selling Designs</LanguageTranslator>
                <span className='ml-1 text-[11px] font-normal'>(By Sales Qty)</span>
            </p>

            <div className='mt-[12px] grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-[20px]'>
                {data?.map((item, idx) => <DesignCard key={item?.rank || idx} item={item} />)}
            </div>
        </div>
    )
}

export default TopSellingDesigns
